import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { usePresentation } from '../hooks/usePresentation.js';
import { useDictation } from '../hooks/useDictation.js';
import { polishWithClaude } from '../utils/polishWithClaude.js';
import ToolHeader from '../components/ToolHeader.jsx';
import DragHandle from '../components/DragHandle.jsx';
import { estimateContentPages } from './Logistics.jsx';
import {
  genId,
  ensureIds,
  deriveInitialPhase,
  isChunkBlank,
} from '../lib/draftModel.js';
import './Draft.css';

/**
 * 03 · Draft — replaces the old Brainstorm + Design tools.
 *
 * Two phases over ONE data model (the existing `design` slice, so the
 * Builder pipeline is untouched):
 *
 *   CAPTURE — a loose brain-dump. No sections, no counts, no "this
 *     becomes a bullet" framing. Just get ideas out, one per line.
 *
 *   SHAPE — group the captured ideas into sections and order them;
 *     then, per point, write the headline + speaker notes (with mic
 *     dictation + ✨ AI polish), set time, add an optional discussion
 *     break and a transition line. This is where structure appears —
 *     only once the user chooses to converge.
 *
 * Existing users (who already have section content) open straight in
 * SHAPE with all their work intact (folded in by migratePlanData on
 * load). Fresh plans open in CAPTURE.
 */

const MAX_SUBTOPICS = 6;       // soft cap for the "+ Add point" button
const SECTION_COLORS = 6;
const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth'];
const ordinalLabel = (i) => ORDINALS[i] || `Point ${i + 1}`;

// Soft length nudge for point headlines (Shape only — Capture has none).
const TITLE_WARN_AT = 60;
const TITLE_DANGER_AT = 90;

function newCaptureItem(text = '') {
  return { id: genId(), text };
}
function newPoint(title = '') {
  return { id: genId(), time: '', title, dictation: '' };
}
function newSection(color = 1) {
  return {
    id: genId(),
    title: '',
    color,
    subtopics: [],
    discussion: { open: false, type: '', prompt: '', time: '' },
    transition: '',
  };
}
function nextSectionColor(chunks) {
  const used = chunks.map((c) => c.color);
  for (let i = 1; i <= SECTION_COLORS; i++) if (!used.includes(i)) return i;
  return (chunks.length % SECTION_COLORS) + 1;
}
function pointMinutes(s) {
  return parseFloat(s.time) || 0;
}
function sectionTotal(section) {
  let t = (section.subtopics ?? []).reduce((acc, s) => acc + pointMinutes(s), 0);
  if (section.discussion?.open) t += parseFloat(section.discussion.time) || 0;
  return t;
}

export default function Draft() {
  const { data, updateTool, savedFlash } = usePresentation();
  const navigate = useNavigate();

  const design = data.design ?? {};
  const chunks = design.chunks ?? [];
  const capture = design.capture ?? [];
  const duration = design.duration ?? '';

  const [phase, setPhaseState] = useState(() => deriveInitialPhase(design));

  // When the user converges Capture → Shape, we drop the new section at
  // the TOP of the list and remember its id so Shape can put the cursor
  // straight into its title (so it's visible + ready, not buried at the
  // bottom). Cleared once focused.
  const [focusSectionId, setFocusSectionId] = useState(null);

  // One-time normalisation on mount: make sure every point has a stable
  // id (older saves keyed points by index), seed a duration target if
  // empty, and drop any blank "unsorted" items. Blank capture items can't
  // be created by the current flow (ideas are only added with text) — they
  // are leftovers from an earlier version, so we clean them up on load.
  // All additive and Builder-safe.
  useEffect(() => {
    const updates = {};
    const withIds = ensureIds(chunks);
    if (withIds !== chunks) updates.chunks = withIds;
    if (duration === '') {
      const fallback = data.pages?.total || data.logistics?.duration || '';
      if (fallback && chunks.length > 0) updates.duration = String(fallback);
    }
    const cap = design.capture ?? [];
    const prunedCap = cap.filter((c) => (c?.text ?? '').trim());
    if (prunedCap.length !== cap.length) updates.capture = prunedCap;
    if (Object.keys(updates).length > 0) updateTool('design', updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPhase = (p) => {
    setPhaseState(p);
    updateTool('design', { phase: p });
  };

  const objective = useMemo(() => {
    const b = data.objective?.built;
    return b ? `${b.prefix} ${b.verb} ${b.task}.` : null;
  }, [data.objective]);

  return (
    <div className="draft">
      <ToolHeader num="03" title="Draft" savedFlash={savedFlash} />

      <div className="dr-body">
        <ObjectiveBanner objective={objective} onGo={() => navigate('/tools/objective')} />

        <PhaseTabs
          phase={phase}
          hasSections={chunks.some((c) => !isChunkBlank(c))}
          onPhase={setPhase}
        />

        {phase === 'capture' ? (
          <GroupPhase
            capture={capture}
            chunks={chunks}
            logistics={data.logistics}
            updateTool={updateTool}
            onContinue={() => setPhase('shape')}
          />
        ) : (
          <ShapePhase
            chunks={chunks}
            duration={duration}
            updateTool={updateTool}
            focusSectionId={focusSectionId}
            onFocusHandled={() => setFocusSectionId(null)}
            onBackToCapture={() => setPhase('capture')}
            onContinue={() => navigate('/tools/builder')}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════ Shared bits ═══════════════════════ */

function ObjectiveBanner({ objective, onGo }) {
  if (objective) {
    return (
      <div className="dr-obj-banner">
        <span className="eyebrow" style={{ color: 'var(--forest-mid)' }}>Your objective</span>
        <p className="dr-obj-text">{objective}</p>
      </div>
    );
  }
  return (
    <button type="button" className="dr-obj-empty" onClick={onGo}>
      <span className="eyebrow" style={{ color: 'var(--terra)' }}>No objective yet</span>
      <span className="dr-obj-empty-cta">Set one in the Objective tool first → it keeps your draft focused</span>
    </button>
  );
}

function PhaseTabs({ phase, hasSections, onPhase }) {
  return (
    <div className="dr-phasetabs" role="tablist" aria-label="Draft phase">
      <button
        type="button"
        role="tab"
        aria-selected={phase === 'capture'}
        className={`dr-phasetab ${phase === 'capture' ? 'is-on' : ''}`}
        onClick={() => onPhase('capture')}
      >
        <span className="dr-phasetab-num">1</span>
        <span className="dr-phasetab-label">Brainstorm &amp; group</span>
        <span className="dr-phasetab-sub">Get ideas out, label them</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={phase === 'shape'}
        className={`dr-phasetab ${phase === 'shape' ? 'is-on' : ''}`}
        onClick={() => onPhase('shape')}
        disabled={!hasSections && phase === 'capture'}
        title={!hasSections ? 'Add some ideas and group them first' : undefined}
      >
        <span className="dr-phasetab-num">2</span>
        <span className="dr-phasetab-label">Flesh out &amp; polish</span>
        <span className="dr-phasetab-sub">Notes, timing, refine</span>
      </button>
    </div>
  );
}

/* ═══════════════════════ PHASE 1 · BRAINSTORM & GROUP ═══════════════════════ */
/*
 * Brain-dump ideas, then *label* each one with the section it belongs to.
 * Grouping is a tap (the "Assign" menu), never a sideways drag — that's what
 * made the old sticky-board fight the phone. Sections assemble themselves as
 * you label. Section order is changed with up/down (also tap, no drag). All
 * of this writes straight to the existing design.chunks / design.capture, so
 * Phase 2 (Flesh out & polish) and the Builder are untouched.
 */

function GroupPhase({ capture, chunks, logistics, updateTool, onContinue }) {
  const [draft, setDraft] = useState('');
  const [assignFor, setAssignFor] = useState(null); // { kind:'idea'|'point', id } | null
  const [focusSectionId, setFocusSectionId] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const setDesign = (patch) => updateTool('design', patch);
  const closeMenu = () => setAssignFor(null);

  const budget = estimateContentPages(logistics?.duration, logistics?.pace);
  const unsortedCount = capture.length;

  function commitDraft() {
    const t = draft.trim();
    if (!t) return;
    setDesign({ capture: [newCaptureItem(t), ...capture] }); // newest on top
    setDraft('');
  }

  const editIdea = (id, text) =>
    setDesign({ capture: capture.map((c) => (c.id === id ? { ...c, text } : c)) });
  const deleteIdea = (id) =>
    setDesign({ capture: capture.filter((c) => c.id !== id) });

  function clearUnsorted() {
    if (capture.length === 0) return;
    if (confirm(`Clear all ${capture.length} unsorted ${capture.length === 1 ? 'idea' : 'ideas'}? This can't be undone.`)) {
      setDesign({ capture: [] });
    }
  }

  const renameSection = (id, title) =>
    setDesign({ chunks: chunks.map((c) => (c.id === id ? { ...c, title } : c)) });

  function moveSection(id, dir) {
    const i = chunks.findIndex((c) => c.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= chunks.length) return;
    setDesign({ chunks: arrayMove(chunks, i, j) });
  }

  function deleteSection(id) {
    const sec = chunks.find((c) => c.id === id);
    if (!sec) return;
    const pts = sec.subtopics ?? [];
    const msg = pts.length
      ? `Remove "${sec.title || 'this section'}"? Its ${pts.length} ${pts.length === 1 ? 'idea' : 'ideas'} move back to Unsorted.`
      : 'Remove this empty section?';
    if (!confirm(msg)) return;
    const back = pts.map((s) => ({ id: s.id, text: s.title || '' }));
    setDesign({ capture: [...capture, ...back], chunks: chunks.filter((c) => c.id !== id) });
  }

  const editPoint = (sectionId, pointId, text) =>
    setDesign({
      chunks: chunks.map((c) =>
        c.id === sectionId
          ? { ...c, subtopics: c.subtopics.map((s) => (s.id === pointId ? { ...s, title: text } : s)) }
          : c
      ),
    });

  const deletePoint = (sectionId, pointId) =>
    setDesign({
      chunks: chunks.map((c) =>
        c.id === sectionId ? { ...c, subtopics: c.subtopics.filter((s) => s.id !== pointId) } : c
      ),
    });

  /*
   * place — the one move that does the grouping.
   *   item = { id, text, dictation? }
   *   from = 'unsorted' | sectionId
   *   to   = sectionId | 'unsorted' | { newTitle }
   * Removing from source + adding to target in a single write.
   */
  function place(item, from, to) {
    const asPoint = () => ({ id: item.id, time: '', title: item.text, dictation: item.dictation ?? '' });
    const asIdea = () => ({ id: item.id, text: item.text });

    let nextCapture = capture;
    let nextChunks = chunks;

    if (from === 'unsorted') {
      nextCapture = nextCapture.filter((c) => c.id !== item.id);
    } else {
      nextChunks = nextChunks.map((c) =>
        c.id === from ? { ...c, subtopics: c.subtopics.filter((s) => s.id !== item.id) } : c
      );
    }

    let newFocus = null;
    if (to === 'unsorted') {
      nextCapture = [...nextCapture, asIdea()];
    } else if (typeof to === 'object' && to.newTitle != null) {
      const section = {
        ...newSection(nextSectionColor(nextChunks)),
        title: to.newTitle.trim(),
        subtopics: [asPoint()],
      };
      if (!to.newTitle.trim()) newFocus = section.id;
      nextChunks = [...nextChunks, section];
    } else {
      nextChunks = nextChunks.map((c) =>
        c.id === to ? { ...c, subtopics: [...(c.subtopics ?? []), asPoint()] } : c
      );
    }

    setDesign({ capture: nextCapture, chunks: nextChunks });
    if (newFocus) setFocusSectionId(newFocus);
    closeMenu();
  }

  function addEmptySection() {
    const section = newSection(nextSectionColor(chunks));
    setDesign({ chunks: [...chunks, section] });
    setFocusSectionId(section.id);
  }

  const toggleCollapse = (id) => setCollapsed((m) => ({ ...m, [id]: !m[id] }));

  const sectionsForMenu = chunks.map((c) => ({
    id: c.id,
    title: c.title || 'Untitled section',
    color: c.color,
  }));

  return (
    <div className="dr-group">
      <div className="dr-intro">
        <p>
          Get every idea out first, then tap <strong>Assign</strong> on each one to label it with
          the section it belongs to. Sections build themselves as you go — no dragging. You'll write
          the talking points and speaker notes in the next step.
        </p>
        {budget && (
          <p className="dr-hint">
            Aiming for roughly <strong>{budget.pages.toFixed(0)} pages of content</strong> for your{' '}
            {logistics?.duration}-minute slot — a loose target, not a limit.
          </p>
        )}
      </div>

      <div className="dr-add-idea">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }}
          placeholder="Type an idea, then press Enter…"
          aria-label="Add an idea"
        />
        <button type="button" className="dr-add-idea-btn" onClick={commitDraft} aria-label="Add idea">+</button>
      </div>

      <div className="dr-unsorted">
        <div className="dr-group-head">
          <span className="eyebrow">◇ Unsorted · {unsortedCount}</span>
          {unsortedCount > 0 && (
            <button type="button" className="btn-text danger" onClick={clearUnsorted}>Clear</button>
          )}
        </div>
        {unsortedCount === 0 ? (
          <div className="dr-unsorted-empty">
            {chunks.length === 0
              ? 'Add a few ideas above to get started.'
              : 'Everything’s sorted. Add more ideas above, or move on.'}
          </div>
        ) : (
          capture.map((item) => (
            <GroupIdeaRow
              key={item.id}
              item={item}
              menuOpen={assignFor?.kind === 'idea' && assignFor.id === item.id}
              sections={sectionsForMenu}
              onEdit={(t) => editIdea(item.id, t)}
              onDelete={() => deleteIdea(item.id)}
              onOpenMenu={() => setAssignFor({ kind: 'idea', id: item.id })}
              onCloseMenu={closeMenu}
              onAssign={(to) => place(item, 'unsorted', to)}
            />
          ))
        )}
      </div>

      {chunks.map((section, idx) => (
        <GroupSection
          key={section.id}
          section={section}
          index={idx}
          count={chunks.length}
          collapsed={!!collapsed[section.id]}
          autoFocus={section.id === focusSectionId}
          onAutoFocused={() => setFocusSectionId(null)}
          sections={sectionsForMenu}
          assignFor={assignFor}
          onRename={(t) => renameSection(section.id, t)}
          onMove={(dir) => moveSection(section.id, dir)}
          onDelete={() => deleteSection(section.id)}
          onToggleCollapse={() => toggleCollapse(section.id)}
          onEditPoint={(pid, t) => editPoint(section.id, pid, t)}
          onDeletePoint={(pid) => deletePoint(section.id, pid)}
          onOpenPointMenu={(pid) => setAssignFor({ kind: 'point', id: pid })}
          onCloseMenu={closeMenu}
          onMovePoint={(point, to) =>
            place({ id: point.id, text: point.title, dictation: point.dictation }, section.id, to)
          }
        />
      ))}

      <button type="button" className="dr-add-section" onClick={addEmptySection}>
        + New section
      </button>

      {unsortedCount > 0 && chunks.length > 0 && (
        <p className="dr-group-note">
          {unsortedCount} {unsortedCount === 1 ? 'idea' : 'ideas'} still unsorted — assign to a
          section to include in your deck.
        </p>
      )}

      <button type="button" className="btn btn-primary dr-continue" onClick={onContinue}>
        Flesh out &amp; polish →
      </button>
    </div>
  );
}

/* The inline "assign to…" picker, shared by unsorted ideas and grouped points. */
function AssignPanel({ sections, currentId, allowUnsorted, onPick, onClose }) {
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (creating) inputRef.current?.focus(); }, [creating]);

  function submitNew() {
    onPick({ newTitle: title });
    setTitle('');
    setCreating(false);
  }

  return (
    <div className="dr-assign" role="menu">
      <div className="dr-assign-head">
        <span className="eyebrow">Assign to…</span>
        <button type="button" className="dr-assign-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="dr-assign-list">
        {sections.length === 0 && !creating && (
          <span className="dr-unsorted-empty">No sections yet — make one below.</span>
        )}
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`dr-assign-opt c${s.color} ${s.id === currentId ? 'is-current' : ''}`}
            onClick={() => onPick(s.id)}
            disabled={s.id === currentId}
          >
            <span className="dr-assign-dot" />
            {s.title}
            {s.id === currentId && <span className="dr-assign-here"> · here</span>}
          </button>
        ))}
        {allowUnsorted && (
          <button type="button" className="dr-assign-opt is-unsorted" onClick={() => onPick('unsorted')}>
            ◇ Move back to Unsorted
          </button>
        )}
      </div>
      {creating ? (
        <div className="dr-assign-new">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } }}
            placeholder="New section name…"
          />
          <button type="button" className="btn btn-primary dr-assign-new-go" onClick={submitNew}>Add</button>
        </div>
      ) : (
        <button type="button" className="dr-assign-newbtn" onClick={() => setCreating(true)}>+ New section…</button>
      )}
    </div>
  );
}

function GroupIdeaRow({ item, menuOpen, sections, onEdit, onDelete, onOpenMenu, onCloseMenu, onAssign }) {
  return (
    <div className={`dr-idea ${menuOpen ? 'is-menu' : ''}`}>
      <div className="dr-idea-row">
        <input
          type="text"
          className="dr-idea-text"
          value={item.text}
          onChange={(e) => onEdit(e.target.value)}
          placeholder="Idea…"
        />
        <button
          type="button"
          className="dr-assign-btn"
          onClick={menuOpen ? onCloseMenu : onOpenMenu}
          aria-expanded={menuOpen}
        >
          Assign ▾
        </button>
        <button type="button" className="dr-idea-del" onClick={onDelete} aria-label="Delete idea">×</button>
      </div>
      {menuOpen && (
        <AssignPanel
          sections={sections}
          currentId={null}
          allowUnsorted={false}
          onPick={onAssign}
          onClose={onCloseMenu}
        />
      )}
    </div>
  );
}

function GroupSection({
  section, index, count, collapsed, autoFocus, onAutoFocused, sections, assignFor,
  onRename, onMove, onDelete, onToggleCollapse,
  onEditPoint, onDeletePoint, onOpenPointMenu, onCloseMenu, onMovePoint,
}) {
  const titleRef = useRef(null);
  useEffect(() => {
    if (autoFocus && titleRef.current) {
      titleRef.current.focus();
      titleRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      onAutoFocused?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const points = section.subtopics ?? [];

  return (
    <div className={`dr-gsection c${section.color}`}>
      <div className="dr-gsection-head">
        <div className="dr-gsection-reorder">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move section up">↑</button>
          <button type="button" onClick={() => onMove(1)} disabled={index === count - 1} aria-label="Move section down">↓</button>
        </div>
        <input
          ref={titleRef}
          type="text"
          className="dr-gsection-title"
          value={section.title}
          onChange={(e) => onRename(e.target.value)}
          placeholder="Section title…"
        />
        <span className="dr-gsection-count">{points.length}</span>
        <button
          type="button"
          className="dr-gsection-collapse"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand section' : 'Collapse section'}
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <button type="button" className="dr-gsection-del" onClick={onDelete} aria-label="Remove section">×</button>
      </div>

      {!collapsed && (
        <div className="dr-gsection-points">
          {points.length === 0 && (
            <div className="dr-gsection-empty">No ideas here yet — assign some from Unsorted.</div>
          )}
          {points.map((p) => (
            <GroupPointRow
              key={p.id}
              point={p}
              section={section}
              sections={sections}
              menuOpen={assignFor?.kind === 'point' && assignFor.id === p.id}
              onEdit={(t) => onEditPoint(p.id, t)}
              onDelete={() => onDeletePoint(p.id)}
              onOpenMenu={() => onOpenPointMenu(p.id)}
              onCloseMenu={onCloseMenu}
              onMove={(to) => onMovePoint(p, to)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupPointRow({ point, section, sections, menuOpen, onEdit, onDelete, onOpenMenu, onCloseMenu, onMove }) {
  return (
    <div className={`dr-gpoint ${menuOpen ? 'is-menu' : ''}`}>
      <div className="dr-gpoint-row">
        <input
          type="text"
          className="dr-gpoint-text"
          value={point.title}
          onChange={(e) => onEdit(e.target.value)}
          placeholder="Point…"
        />
        <button
          type="button"
          className={`dr-gpoint-pill c${section.color}`}
          onClick={menuOpen ? onCloseMenu : onOpenMenu}
          aria-expanded={menuOpen}
          title="Move to another section"
        >
          {section.title || 'Section'} ▾
        </button>
        <button type="button" className="dr-idea-del" onClick={onDelete} aria-label="Delete point">×</button>
      </div>
      {menuOpen && (
        <AssignPanel
          sections={sections}
          currentId={section.id}
          allowUnsorted={true}
          onPick={onMove}
          onClose={onCloseMenu}
        />
      )}
    </div>
  );
}

/* ═══════════════════════ SHAPE PHASE ═══════════════════════ */

function ShapePhase({ chunks, duration, updateTool, focusSectionId, onFocusHandled, onBackToCapture, onContinue }) {
  const setChunks = (next) => updateTool('design', { chunks: next });

  /* ── mutators ── */
  const updateChunk = (id, patch) =>
    setChunks(chunks.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  function deleteChunk(id) {
    if (confirm('Remove this section and everything inside it?')) {
      setChunks(chunks.filter((c) => c.id !== id));
    }
  }
  function addChunk() {
    setChunks([...chunks, newSection(nextSectionColor(chunks))]);
  }
  function addPoint(chunkId) {
    setChunks(
      chunks.map((c) => {
        if (c.id !== chunkId) return c;
        if ((c.subtopics?.length ?? 0) >= MAX_SUBTOPICS) return c;
        return { ...c, subtopics: [...(c.subtopics ?? []), newPoint('')] };
      })
    );
  }
  function updatePoint(chunkId, pointId, patch) {
    setChunks(
      chunks.map((c) =>
        c.id === chunkId
          ? { ...c, subtopics: c.subtopics.map((s) => (s.id === pointId ? { ...s, ...patch } : s)) }
          : c
      )
    );
  }
  function deletePoint(chunkId, pointId) {
    setChunks(
      chunks.map((c) =>
        c.id === chunkId
          ? { ...c, subtopics: c.subtopics.filter((s) => s.id !== pointId) }
          : c
      )
    );
  }
  function updateDiscussion(chunkId, patch) {
    setChunks(
      chunks.map((c) =>
        c.id === chunkId ? { ...c, discussion: { ...c.discussion, ...patch } } : c
      )
    );
  }

  /* ── budget ── */
  const runningTotal = useMemo(
    () => chunks.reduce((sum, c) => sum + sectionTotal(c), 0),
    [chunks]
  );
  const target = parseFloat(duration) || 0;
  const diff = target - runningTotal;

  /* ── drag-and-drop: sections reorder + points reorder / move across ── */
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } })
  );

  const findPointSection = (pointId) =>
    chunks.find((c) => (c.subtopics ?? []).some((s) => s.id === pointId));

  function resolveSectionFromOverId(overId) {
    const s = String(overId);
    if (s.startsWith('sec-')) return chunks.find((c) => `sec-${c.id}` === s);
    if (s.startsWith('secbody-')) return chunks.find((c) => `secbody-${c.id}` === s);
    if (s.startsWith('pt-')) return findPointSection(Number(s.replace('pt-', '')));
    return null;
  }

  function handleDragEnd(event) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeStr = String(active.id);
    const overStr = String(over.id);

    // Section reorder
    if (activeStr.startsWith('sec-') && overStr.startsWith('sec-')) {
      const from = chunks.findIndex((c) => `sec-${c.id}` === activeStr);
      const to = chunks.findIndex((c) => `sec-${c.id}` === overStr);
      if (from >= 0 && to >= 0 && from !== to) setChunks(arrayMove(chunks, from, to));
      return;
    }

    // Point reorder / cross-section move
    if (activeStr.startsWith('pt-')) {
      const activePointId = Number(activeStr.replace('pt-', ''));
      const source = findPointSection(activePointId);
      if (!source) return;
      const dest = resolveSectionFromOverId(overStr);
      if (!dest) return;

      // Same section → reorder
      if (source.id === dest.id) {
        if (!overStr.startsWith('pt-')) return;
        const overPointId = Number(overStr.replace('pt-', ''));
        const from = source.subtopics.findIndex((s) => s.id === activePointId);
        const to = source.subtopics.findIndex((s) => s.id === overPointId);
        if (from < 0 || to < 0 || from === to) return;
        setChunks(
          chunks.map((c) =>
            c.id === source.id ? { ...c, subtopics: arrayMove(c.subtopics, from, to) } : c
          )
        );
        return;
      }

      // Across sections → remove from source, insert into dest
      const moving = source.subtopics.find((s) => s.id === activePointId);
      if (!moving) return;
      const next = chunks.map((c) => {
        if (c.id === source.id) {
          return { ...c, subtopics: c.subtopics.filter((s) => s.id !== activePointId) };
        }
        if (c.id === dest.id) {
          let insertAt = c.subtopics.length;
          if (overStr.startsWith('pt-')) {
            const overPointId = Number(overStr.replace('pt-', ''));
            const idx = c.subtopics.findIndex((s) => s.id === overPointId);
            if (idx >= 0) insertAt = idx;
          }
          const nextPts = [...c.subtopics];
          nextPts.splice(insertAt, 0, moving);
          return { ...c, subtopics: nextPts };
        }
        return c;
      });
      setChunks(next);
    }
  }

  return (
    <div className="dr-shape">
      {/* Budget */}
      <div className={`dr-timing ${target > 0 && diff < 0 ? 'is-over' : ''}`}>
        <div className="dr-timing-left">
          <span className="eyebrow">Running total</span>
          <span className="dr-timing-total">{runningTotal} min</span>
        </div>
        <div className="dr-timing-right">
          <div className="dr-timing-target">
            <span className="eyebrow">Target</span>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={duration}
              onChange={(e) => updateTool('design', { duration: e.target.value })}
              placeholder="0"
              className="dr-target-input"
              aria-label="Target duration in minutes"
            />
            <span className="eyebrow" style={{ marginLeft: 4 }}>min</span>
          </div>
          <div className="dr-budget-display">
            {!target && 'Set a target to track budget'}
            {target > 0 && diff > 0 && `${diff} min remaining`}
            {target > 0 && diff === 0 && `Right on budget — ${target} min`}
            {target > 0 && diff < 0 && `Over by ${Math.abs(diff)} min`}
          </div>
        </div>
      </div>

      <div className="dr-tip">
        <strong>Tip:</strong> each <strong>point</strong> becomes a bullet on its section's
        slide. Write a short headline, then tap 🎙️ to record what you'll say (or type it) —
        that becomes your speaker notes, which the audience never sees. Tap ✨ to polish either
        field. Drag points between sections to regroup.
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveId(e.active.id)}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={chunks.map((c) => `sec-${c.id}`)} strategy={verticalListSortingStrategy}>
          <div className="dr-sections">
            {chunks.map((chunk, idx) => (
              <SortableSection
                key={chunk.id}
                chunk={chunk}
                num={idx + 1}
                total={sectionTotal(chunk)}
                onTitleChange={(t) => updateChunk(chunk.id, { title: t })}
                onDelete={() => deleteChunk(chunk.id)}
                onAddPoint={() => addPoint(chunk.id)}
                onPointChange={(pid, patch) => updatePoint(chunk.id, pid, patch)}
                onPointDelete={(pid) => deletePoint(chunk.id, pid)}
                onDiscussionChange={(patch) => updateDiscussion(chunk.id, patch)}
                onTransitionChange={(t) => updateChunk(chunk.id, { transition: t })}
                autoFocusTitle={chunk.id === focusSectionId}
                onAutoFocused={onFocusHandled}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? <ShapeDragOverlay activeId={activeId} chunks={chunks} /> : null}
        </DragOverlay>
      </DndContext>

      <button type="button" className="dr-add-section" onClick={addChunk}>
        + Add another section
      </button>

      <div className="dr-shape-foot">
        <button type="button" className="btn-text" onClick={onBackToCapture}>
          ← Back to brainstorm &amp; group
        </button>
      </div>

      <button type="button" className="btn btn-primary dr-continue" onClick={onContinue}>
        Continue to Visuals →
      </button>
    </div>
  );
}

/* ── Sortable section ── */
function SortableSection({
  chunk, num, total,
  onTitleChange, onDelete, onAddPoint, onPointChange, onPointDelete,
  onDiscussionChange, onTransitionChange,
  autoFocusTitle, onAutoFocused,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `sec-${chunk.id}`,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const colorClass = `c${chunk.color}`;

  const { setNodeRef: setBodyRef, isOver } = useDroppable({ id: `secbody-${chunk.id}` });
  const points = chunk.subtopics ?? [];

  // When this section was just created by converging Capture, drop the
  // cursor into its title and scroll it into view, then tell the parent
  // to clear the request so we don't refocus on later renders.
  const titleRef = useRef(null);
  useEffect(() => {
    if (autoFocusTitle && titleRef.current) {
      titleRef.current.focus();
      titleRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      onAutoFocused?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusTitle]);

  return (
    <div ref={setNodeRef} style={style} className={`dr-section ${colorClass} ${isDragging ? 'is-dragging' : ''}`}>
      <header className="dr-section-head">
        <DragHandle listeners={listeners} attributes={attributes} label="Drag to reorder section" />
        <span className="dr-section-badge">Section {num}</span>
        <input
          ref={titleRef}
          type="text"
          value={chunk.title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Title — what's the main point of this section?"
          className="dr-section-title"
        />
        <PolishButton text={chunk.title} kind="chunk-title" onPolished={onTitleChange} aria-label="Polish section title with Claude" />
        <button type="button" className="dr-section-remove" onClick={onDelete} aria-label="Remove section">×</button>
      </header>

      <div className="dr-section-meta">
        <span className="eyebrow">Section time</span>
        <strong>{total} min</strong>
      </div>

      <div ref={setBodyRef} className={`dr-points ${isOver ? 'is-drop-target' : ''}`}>
        <SortableContext items={points.map((s) => `pt-${s.id}`)} strategy={verticalListSortingStrategy}>
          {points.length === 0 && (
            <div className="dr-points-empty">{isOver ? 'Drop here' : 'No points yet — add one below, or drag one in.'}</div>
          )}
          {points.map((sub, i) => (
            <SortablePoint
              key={sub.id}
              sub={sub}
              label={ordinalLabel(i)}
              onChange={(patch) => onPointChange(sub.id, patch)}
              onDelete={() => onPointDelete(sub.id)}
            />
          ))}
        </SortableContext>
      </div>

      {points.length < MAX_SUBTOPICS && (
        <button type="button" className="dr-add-point" onClick={onAddPoint}>+ Add point</button>
      )}

      <div className={`dr-disc ${chunk.discussion?.open ? 'is-open' : ''}`}>
        <div className="dr-disc-head">
          <span className="dr-disc-label">★ Discussion break (optional)</span>
          <button type="button" className="dr-disc-toggle" onClick={() => onDiscussionChange({ open: !chunk.discussion?.open })}>
            {chunk.discussion?.open ? '– Hide' : '+ Add'}
          </button>
        </div>
        {chunk.discussion?.open && (
          <div className="dr-disc-body">
            <div className="dr-disc-time">
              <span className="eyebrow">Time</span>
              <input
                type="number" inputMode="numeric" min="0"
                value={chunk.discussion.time}
                onChange={(e) => onDiscussionChange({ time: e.target.value })}
                placeholder="0" className="dr-point-time-input" aria-label="Discussion break minutes"
              />
              <span className="eyebrow">min</span>
            </div>
            <label className="eyebrow">Discussion type</label>
            <input
              type="text"
              value={chunk.discussion.type}
              onChange={(e) => onDiscussionChange({ type: e.target.value })}
              placeholder="e.g. Turn-and-talk · Round robin · Silent reflection"
              className="dr-disc-type-input"
            />
            <label className="eyebrow" style={{ marginTop: 6 }}>Instructions / question</label>
            <textarea
              value={chunk.discussion.prompt}
              onChange={(e) => onDiscussionChange({ prompt: e.target.value })}
              rows={2}
              placeholder="What will you ask the group to do or discuss?"
            />
          </div>
        )}
      </div>

      <div className="dr-transition">
        <label className="eyebrow">↳ Transition or closing comments</label>
        <textarea
          value={chunk.transition}
          onChange={(e) => onTransitionChange(e.target.value)}
          rows={2}
          placeholder="How will you bridge to the next section? (Or your closing, if this is the last one.)"
        />
      </div>
    </div>
  );
}

/* ── Sortable point (wraps the rich editor) ── */
function SortablePoint({ sub, label, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `pt-${sub.id}`,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const dictation = useDictation((finalText) => {
    const next = (sub.dictation ? sub.dictation + ' ' : '') + finalText;
    onChange({ dictation: next });
  });

  const titleLen = (sub.title ?? '').length;
  let counterClass = 'dr-point-counter';
  let counterMsg = '';
  if (titleLen >= TITLE_DANGER_AT) {
    counterClass += ' is-danger';
    counterMsg = "Probably won't fit on a slide — trim or split into two";
  } else if (titleLen >= TITLE_WARN_AT) {
    counterClass += ' is-warn';
    counterMsg = 'Getting long for a slide bullet';
  }

  return (
    <div ref={setNodeRef} style={style} className={`dr-point ${isDragging ? 'is-dragging' : ''}`}>
      <div className="dr-point-rail">
        <DragHandle listeners={listeners} attributes={attributes} label="Drag to reorder or move point" className="dr-point-handle" />
        <div className="dr-point-time">
          <input
            type="number" inputMode="numeric" min="0"
            value={sub.time}
            onChange={(e) => onChange({ time: e.target.value })}
            placeholder="0" className="dr-point-time-input" aria-label={`${label} point minutes`}
          />
          <span className="eyebrow">min</span>
        </div>
      </div>

      <div className="dr-point-body">
        <div className="dr-point-head">
          <label className="eyebrow">{label} point</label>
          <button type="button" className="dr-point-clear" onClick={onDelete} aria-label={`Remove ${label.toLowerCase()} point`} title="Remove this point">×</button>
        </div>

        <div className="dr-point-field">
          <div className="dr-point-field-head">
            <label className="eyebrow">Point — one short line your audience will see</label>
            <PolishButton text={sub.title} kind="bit-title" onPolished={(t) => onChange({ title: t })} size="sm" aria-label="Polish point with Claude" />
          </div>
          <input
            type="text"
            value={sub.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="The one short line your audience will see"
            className="dr-point-title-input"
          />
          {titleLen > 0 && (
            <div className={counterClass} aria-live="polite">
              <span>{titleLen} chars</span>
              {counterMsg && <span> · {counterMsg}</span>}
            </div>
          )}
        </div>

        <div className="dr-point-field">
          <div className="dr-point-field-head">
            <label className="eyebrow">
              What you'll say — becomes speaker notes
              <span className="dr-point-hint"> · tap 🎙️ to dictate · tap ✨ to polish</span>
            </label>
            <div className="dr-point-actions">
              {dictation.supported && (
                <button
                  type="button"
                  className={`dr-mic-btn ${dictation.listening ? 'is-recording' : ''}`}
                  onClick={dictation.listening ? dictation.stop : dictation.start}
                  aria-label={dictation.listening ? 'Stop recording' : 'Record dictation'}
                  aria-pressed={dictation.listening}
                  title={dictation.listening ? 'Stop recording' : 'Tap to record. Tap again to stop.'}
                >
                  {dictation.listening ? '■' : '🎙️'}
                </button>
              )}
              <PolishButton text={sub.dictation} kind="dictation" onPolished={(t) => onChange({ dictation: t })} size="sm" aria-label="Polish speaker notes with Claude" />
            </div>
          </div>
          <textarea
            value={sub.dictation}
            onChange={(e) => onChange({ dictation: e.target.value })}
            rows={4}
            placeholder="Tap the mic and just talk — or type. Doesn't have to be polished; that's what ✨ is for."
            className={`dr-point-dictation ${dictation.listening ? 'is-recording' : ''}`}
          />
          {!dictation.supported && (
            <span className="dr-point-mic-unsupported">Voice dictation isn't available in this browser — type or paste here.</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Polish button (unchanged behaviour from Design) ── */
function PolishButton({ text, kind, onPolished, size, ...rest }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const empty = !text || !text.trim();

  async function handleClick() {
    if (busy || empty) return;
    setError('');
    setBusy(true);
    try {
      const polished = await polishWithClaude(text, kind);
      if (polished) onPolished(polished);
    } catch (e) {
      setError(e.message || 'Polish failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="dr-polish-wrap">
      <button
        type="button"
        className={`dr-polish-btn ${size === 'sm' ? 'is-sm' : ''} ${busy ? 'is-busy' : ''}`}
        onClick={handleClick}
        disabled={busy || empty}
        title={empty ? 'Nothing to polish yet' : 'Polish with Claude'}
        {...rest}
      >
        {busy ? '…' : '✨'}
      </button>
      {error && <span className="dr-polish-error" role="alert">{error}</span>}
    </span>
  );
}

/* ── Drag overlay ── */
function ShapeDragOverlay({ activeId, chunks }) {
  const id = String(activeId);
  if (id.startsWith('sec-')) {
    const chunk = chunks.find((c) => `sec-${c.id}` === id);
    if (!chunk) return null;
    return (
      <div className={`dr-section c${chunk.color} dr-section-overlay`}>
        <header className="dr-section-head">
          <span className="dr-section-badge">Section</span>
          <span className="dr-section-title" style={{ pointerEvents: 'none' }}>
            {chunk.title || 'Untitled section'}
          </span>
        </header>
        <div className="dr-section-overlay-meta">{(chunk.subtopics ?? []).length} points</div>
      </div>
    );
  }
  if (id.startsWith('pt-')) {
    const pid = Number(id.replace('pt-', ''));
    let pt, chunk;
    for (const c of chunks) {
      const f = (c.subtopics ?? []).find((s) => s.id === pid);
      if (f) { pt = f; chunk = c; break; }
    }
    if (!pt) return null;
    return <div className={`dr-point-overlay c${chunk.color}`}>{pt.title || '(empty point)'}</div>;
  }
  return null;
}
